// src/components/NotificationBell.jsx
import { useState, useRef, useEffect } from "react";
import { Bell, CheckCheck, MessageCircle, User } from "lucide-react";
import { useNotifications } from "../hooks/useNotifications";
import { useLanguage } from "../context/LanguageContext";

export default function NotificationBell() {
  const { notifications, unreadCount, markAllRead } = useNotifications();
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handleClick(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  function handleOpen() {
    setOpen(o => !o);
    if (!open && unreadCount > 0) markAllRead();
  }

  function formatTime(ts) {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString( { hour: "2-digit", minute: "2-digit" });
  }

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <button className="notif-btn" onClick={handleOpen} aria-label={t("notifications")}>
        <Bell size={22} />
        {unreadCount > 0 && (
          <span className="notif-badge">{unreadCount > 9 ? "9+" : toArabicNum(unreadCount)}</span>
        )}
      </button>

      {open && (
        <div className="notif-dropdown slide-down" role="dialog" aria-label={t("notifications")}>
          <div style={{
            padding: "0.85rem 1rem",
            borderBottom: "1px solid var(--color-gray-100)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
          }}>
            <span style={{ fontWeight: 700, fontSize: "0.95rem" }}>{t("notifications")}</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="btn btn-ghost btn-sm" style={{ fontSize: "0.78rem" }}>
                <CheckCheck size={14} /> {t("markRead")}
              </button>
            )}
          </div>

          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            {notifications.length === 0 ? (
              <div style={{ padding: "2rem", textAlign: "center", color: "var(--color-gray-400)", fontSize: "0.875rem" }}>
                <Bell size={28} style={{ opacity: 0.3, display: "block", margin: "0 auto 0.5rem" }} />
                {t("noNotifications")}
              </div>
            ) : notifications.map(n => (
              <div key={n.id} className={`notif-item ${!n.read ? "unread" : ""}`}>
                <span style={{ color: "var(--color-forest)", flexShrink: 0 }}>
                  {n.type === "message" ? <MessageCircle size={16} /> : <User size={16} />}
                </span>
                <div>
                  <div style={{ fontSize: "0.875rem" }}>{n.message}</div>
                  <div style={{ fontSize: "0.72rem", color: "var(--color-gray-400)", marginTop: "0.2rem" }}>
                    {formatTime(n.createdAt)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function toArabicNum(n) {
  return n
}
